import {
  Box,
  Flex,
  Heading,
  ModalBody,
  Modal as ModalC,
  ModalCloseButton,
  ModalContent,
  type ModalContentProps,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
} from '@chakra-ui/react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import useAppContext from '@/client/context/app/useAppContext'

interface Props {
  open: boolean
  body: () => React.ReactNode
  onClose: () => void

  title?: string | (() => React.ReactNode) | null
  outlineTitle?: boolean
  size?: ModalProps['size']
  height?: ModalContentProps['height']
  disableClose?: boolean
  disableClickOutside?: boolean
  backgroundColor?: ModalContentProps['backgroundColor']
  initialFocusRef?: React.MutableRefObject<null> | undefined
  removeBodyPaddingAndMargin?: boolean

  rightFooterContent?: (() => React.ReactNode) | null
  leftFooterContent?: (() => React.ReactNode) | null
  rightHeaderContent?: (() => React.ReactNode) | null
}

export default function ModalV3({
  open,
  body,
  onClose,
  title,
  outlineTitle = false,
  size = 'md',
  height,
  disableClose = false,
  disableClickOutside = false,
  backgroundColor = 'bg',
  initialFocusRef,
  removeBodyPaddingAndMargin = false,
  rightFooterContent,
  leftFooterContent,
  rightHeaderContent,
}: Props): React.ReactElement | null {
  const appContext = useAppContext()

  if (appContext.prepass) {
    return (
      <>
        {body()}
        {rightFooterContent?.()}
        {leftFooterContent?.()}
        {rightHeaderContent?.()}
        {typeof title === 'function' ? title() : null}
      </>
    )
  }

  const modalContentProps: {
    height?: ModalContentProps['height']
    overflowY?: ModalContentProps['overflowY']
  } =
    height != null
      ? {
          height,
          overflowY: 'scroll',
        }
      : {}

  return (
    <ModalC
      isOpen={open}
      onClose={onClose}
      preserveScrollBarGap
      closeOnOverlayClick={!disableClickOutside}
      size={size}
      isCentered
      scrollBehavior="outside"
      initialFocusRef={initialFocusRef}
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="8px"
        bgColor={backgroundColor}
        border={`2px solid ${chakraTokenToVar('border.emphasized')}`}
        {...modalContentProps}
      >
        <ModalHeader
          borderBottom={
            outlineTitle ? `2px solid ${chakraTokenToVar('border.emphasized')}` : undefined
          }
          marginRight={disableClose ? '16px' : undefined}
        >
          <Flex direction="row" justify="space-between" align="center" width="100%">
            <Box>
              {title == null ? null : typeof title === 'function' ? (
                title()
              ) : (
                <Heading size="lg" as="h4">
                  {title}
                </Heading>
              )}
            </Box>
            {rightHeaderContent != null ? <Box ml="16px">{rightHeaderContent()}</Box> : null}
          </Flex>
        </ModalHeader>

        {disableClose ? null : (
          <ModalCloseButton
            border="none"
            bgColor={backgroundColor}
            borderRadius="16px"
            _hover={{ bgColor: 'gray.subtle', cursor: 'pointer' }}
          />
        )}
        <ModalBody
          mb={
            rightFooterContent == null && leftFooterContent == null && !removeBodyPaddingAndMargin
              ? '8px'
              : undefined
          }
          p={removeBodyPaddingAndMargin ? 0 : undefined}
          display="flex"
          flexDirection="column"
        >
          {body()}
        </ModalBody>
        {rightFooterContent != null || leftFooterContent != null ? (
          <ModalFooter>
            <Flex justify="space-between" width="100%">
              {leftFooterContent?.() ?? <Box />}
              {rightFooterContent?.() ?? <Box />}
            </Flex>
          </ModalFooter>
        ) : null}
      </ModalContent>
    </ModalC>
  )
}
